import { create } from 'zustand';

/**
 * Scopes (e.g. the dashboard admin area) that need an operator session even
 * while the business-level POS lock setting is disabled. The guard and the
 * lifecycle provider only revoke a session for "operator authentication not
 * required" when no scope is retained, and releasing the last scope lets them
 * end the session immediately (leaving a gated page locks it again).
 *
 * Kept outside `OperatorAuthenticationState` on purpose: demand describes what
 * the currently mounted UI needs, not the session itself, so session resets
 * must never clear it.
 */

type OperatorSessionDemandState = {
  /** One entry per retained scope; duplicates act as reference counts. */
  scopes: string[];
};

export const useOperatorSessionDemandStore = create<OperatorSessionDemandState>(() => ({
  scopes: [],
}));

/** Registers a scope and returns its idempotent release function. */
export function retainOperatorSessionScope(scope: string): () => void {
  useOperatorSessionDemandStore.setState((state) => ({ scopes: [...state.scopes, scope] }));

  let released = false;
  return () => {
    if (released) return;
    released = true;
    useOperatorSessionDemandStore.setState((state) => {
      const index = state.scopes.indexOf(scope);
      if (index === -1) return state;
      const scopes = state.scopes.slice();
      scopes.splice(index, 1);
      return { scopes };
    });
  };
}

export function hasOperatorSessionDemand(): boolean {
  return useOperatorSessionDemandStore.getState().scopes.length > 0;
}
